import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getInvoiceDetails,
  createInvoice,
  editInvoice,
  getTenants,
} from "../../../services/AdminPortalAPIs";
import { InvoiceFormValues } from "src/layouts/AdminPortal/Subscriptions/CreateInvoiceInterface";

import {
  Card,
  DatePicker,
  Button,
  Form,
  Row,
  Space,
  Col,
  Typography,
  message,
  Input,
} from "antd";
import dayjs from "dayjs";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import StyledDropdown from "src/components/reusable/StyledDropdown";

const {  Paragraph } = Typography;
const { TextArea } = Input;

const CreateInvoice: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm<InvoiceFormValues>();
  const [tenants, setTenants] = useState<
    { tenant_name: string; tenant_email: string }[]
  >([]);
  const { invoiceId } = useParams();
  const isEditMode = Boolean(invoiceId);
  const didFetchRef = React.useRef(false);

  const handleFinish = async (values: InvoiceFormValues) => {
    const payload = {
      billing_address: values.billingAddress,
      package_name: values.subscriptionPlan,
      package_price: values.amount || 0,
      next_billing_date: values.nextBillingDate?.format("YYYY-MM-DD") || "",
      status: values.paymentStatus,
      extra_users: values.extraUserCount || 0,
      price_per_extra_user: values.costPerUser || 0,
      notes: values.notes || "",
    };

    try {
      if (isEditMode && invoiceId) {
        const editPayload = { invoice_id: invoiceId, ...payload };
        const res = await editInvoice(editPayload);
        if (!res.data.status || !res.data.data) {
          message.error("Invoice not found");
          navigate("/admin-portal/subscriptions");
        } else if (res.data.status) {
          message.success("Invoice updated successfully");
          navigate("/admin-portal/subscriptions");
        } else {
          message.error(res.data.detail || "Update failed.");
        }
      } else {
        const res = await createInvoice({
          user_email: values.customerEmail,
          ...payload,
        });

        if (res.data.status) {
          message.success("Invoice created successfully");
          navigate("/admin-portal/subscriptions");
        } else {
          message.error(res.data.message || "Creation failed.");
        }
      }
    } catch (error) {
      message.error("Something went wrong.");
    }
  };

  useEffect(() => {
    if (didFetchRef.current) {return;}
    didFetchRef.current = true;
    const fetchTenants = async () => {
      try {
        const res = await getTenants();
        if (res.data.status) {
          setTenants(res.data.data);
        } else {
          console.error("Failed to fetch tenants:", res.data.message);
        }
      } catch (err) {
        console.error("Error fetching tenants:", err);
      }
    };

    fetchTenants();
  }, []);

  useEffect(() => {
    if (tenants.length === 1) {
      const [onlyTenant] = tenants;
      form.setFieldsValue({
        customerName: onlyTenant.tenant_name,
        customerEmail: onlyTenant.tenant_email,
      });
    }
  }, [tenants]);

  useEffect(() => {
    if (isEditMode && invoiceId && tenants.length > 0) {
      const fetchInvoice = async () => {
        try {
          const res = await getInvoiceDetails(invoiceId);
          const data = res.data.data;

          // Match tenant from tenants list
          const matchedTenant = tenants.find(
            t => t.tenant_name === data.tenant_name
          );

          form.setFieldsValue({
            customerName: data.tenant_name,
            customerEmail: data.user_email,
            billingAddress: data.billing_address,
            subscriptionPlan: data.package_name,
            amount: data.package_price,
            nextBillingDate: dayjs(data.next_billing_date),
            paymentStatus: data.status,
            notes: data.notes,
            extraUserCount: data.extra_users,
            costPerUser: data.price_per_extra_user,
          });
        } catch (error) {
          message.error("Failed to fetch invoice details.");
        }
      };

      fetchInvoice();
    }
  }, [isEditMode, invoiceId, tenants]);

  return (
    <div className="Create-invoice-box" >
      <div className="invoice-back-button">
        <button
          className="send-email-btn"
          onClick={() => navigate("/admin-portal/subscriptions")}
        >
          <span>&lt;&nbsp;</span> Go Back
        </button>
      </div>

      <span className="heading-primary">
        {isEditMode ? "Edit Invoice" : "Create Invoice"}
      </span>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          amount: 0,
        }}
      >
      <Row gutter={24} className="customer-row">
  <Col xs={24} md={12}>
    <Card className="customer-card">
      <span className="heading-secondary">Customer Information</span>
      <Space direction="vertical" size="large" className="customer-space" />
      <Paragraph className="customer-paragraph">
        Enter the customer details for this invoice.
      </Paragraph>

              <Form.Item
                label="Customer Name"
                name="customerName"
                rules={[
                  {
                    required: true,
                    message: "Please select the customer name",
                  },
                ]}
              >
                <StyledDropdown
                  placeholder="Select customer"
                  disabled={isEditMode}
                  onChange={value => {
                    const selected = tenants.find(
                      t => t.tenant_name === value
                    );
                    if (selected) {
                      form.setFieldsValue({
                        customerEmail: selected.tenant_email,
                      });
                    }
                  }}
                  options={tenants.map(tenant => ({
                    value: tenant.tenant_name,
                    label: tenant.tenant_name,
                  }))}
                  popupClassName="custom-dropdown-menu"
                />
              </Form.Item>
              <Form.Item label="Customer Email" name="customerEmail">
                <StyledTextInput
                  disabled
                  placeholder={
                    isEditMode
                      ? form.getFieldValue("customerEmail")
                      : "client@example.com"
                  }
                />
              </Form.Item>

              <Form.Item
                label="Billing Address"
                name="billingAddress"
                rules={[
                  { required: true, message: "Please enter billing address" },
                ]}
              >
                <TextArea placeholder="Enter billing address" rows={4} />
              </Form.Item>
            </Card>
          </Col>

          {/* Invoice Details */}
          <Col xs={24} md={12}>
            <Card
             className="customer-card"
            >
              <span className="heading-secondary">Invoice Details</span>
              <Paragraph className="customer-paragraph">
                Configure the invoice payment details.
              </Paragraph>

              <Form.Item
                label="Subscription Plan"
                name="subscriptionPlan"
                rules={[{ required: true, message: "Please select a plan" }]}
              >
                <StyledDropdown
                  placeholder="Select a plan"
                  options={[
                    { value: "basic", label: "Basic" },
                    { value: "trail", label: "Trial" },
                  ]}
                  popupClassName="custom-dropdown-menu"
                />
              </Form.Item>

              <Form.Item
                label="Amount (USD)"
                name="amount"
                rules={[{ required: true, message: "Please enter amount" }]}
              >
                <StyledTextInput type="number" min={0} step={0.01} />
              </Form.Item>
              <Form.Item
                label="Additional Users"
                name="extraUserCount"
                rules={[
                  {
                    required: false,
                    message: "Please enter additional user count",
                  },
                ]}
              >
                <StyledTextInput
                  type="number"
                  min={0}
                  placeholder="Enter number of additional users"
                />
              </Form.Item>

              <Form.Item
                label="Cost per Additional User (USD)"
                name="costPerUser"
                rules={[
                  {
                    required: false,
                    message: "Please enter cost per additional user",
                  },
                ]}
              >
                <StyledTextInput
                  type="number"
                  min={0}
                  step={0.01}
                  placeholder="Enter cost per additional user"
                />
              </Form.Item>

              <Form.Item
                label="Next Billing Date"
                name="nextBillingDate"
                rules={[
                  {
                    required: true,
                    message: "Please select the next billing date",
                  },
                ]}
              >
                <DatePicker className="invoice-datepicker" />
              </Form.Item>

              <Form.Item
                label="Payment Status"
                name="paymentStatus"
                rules={[
                  { required: true, message: "Please select payment status" },
                ]}
              >
                <StyledDropdown
                  placeholder="Select status"
                  options={[
                    { value: "paid", label: "Paid" },
                    { value: "pending", label: "Pending" },
                    { value: "overdue", label: "Overdue" },
                  ]}
                  popupClassName="custom-dropdown-menu"
                />
              </Form.Item>

              <Form.Item label="Notes" name="notes">
                <TextArea placeholder="Additional notes" rows={4} />
              </Form.Item>
            </Card>
          </Col>
        </Row>

        {/* Footer Buttons */}
        <Row justify="end" className="customer-row">
          <Button
          className="button button--outline"
            style={{ marginRight: 8 }}
          aria-label="Cancel"
            onClick={() => navigate("/admin-portal/subscriptions")}
          >
            Cancel
          </Button>
          <Button className="button button--primary" htmlType="submit">
            {isEditMode ? "Update Invoice" : "Create Invoice"}
          </Button>
        </Row>
      </Form>
    </div>
  );
};

export default CreateInvoice;
